// ================================================================
// 交易紀錄管理頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-26
// 功能：交易列表、日期篩選、使用者篩選
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Search, FilterList, Refresh } from '@mui/icons-material';
import {
  getTransactions,
  AdminTransaction,
  Pagination,
  TransactionTypeStats,
} from '../../api/admin';
import AdminLayout from '../../components/admin/AdminLayout';
import { getDynamicPeriodLabel } from '../../api/questions';

const applyPeriodToTransactionDesc = (text: string): string => {
  if (!text) return text;
  const now = new Date();
  const { label: monthLabel } = getDynamicPeriodLabel('monthly', now);
  const { label: weekLabel } = getDynamicPeriodLabel('weekly', now);
  return text
    .replace(/下個月|本月|未來一個月/g, monthLabel)
    .replace(/本週|下週|未來七天/g, weekLabel);
};

// 交易類型中文對應
const transactionTypeLabels: { [key: string]: string } = {
  query: '問命查詢',
  pair: '配對分析',
  admin_adjust: '管理員調整（餘額）',
  admin_adjust_points: '管理員調整（點數）',
  registration_bonus: '註冊贈送',
  recharge: '充值',
  debit: '消費',
  credit: '加值',
  question_deduct: '問答扣款',
};

// 交易類型顏色
const getTypeColor = (type: string): 'success' | 'error' | 'info' | 'warning' | 'default' => {
  switch (type) {
    case 'registration_bonus':
    case 'recharge':
    case 'credit':
      return 'success';
    case 'query':
    case 'pair':
    case 'debit':
    case 'question_deduct':
      return 'error';
    case 'admin_adjust':
    case 'admin_adjust_points':
      return 'info';
    default:
      return 'default';
  }
};

// ================================================================
// 主要組件
// ================================================================

const TransactionManagementPage: React.FC = () => {
  // 狀態
  const [transactions, setTransactions] = useState<AdminTransaction[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });
  const [typeStats, setTypeStats] = useState<TransactionTypeStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 篩選條件
  const [userSearchFilter, setUserSearchFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  // 載入交易紀錄
  const loadTransactions = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getTransactions({
        page,
        limit: 20,
        user_search: userSearchFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
      });

      if (response.success) {
        setTransactions(response.transactions || []);
        if (response.pagination) {
          setPagination({
            page: response.pagination.page,
            page_size: response.pagination.page_size,
            total: response.pagination.total,
            total_pages: response.pagination.total_pages,
          });
        }
        if (response.type_stats) {
          setTypeStats(response.type_stats);
        }
      } else {
        setError(response.message || '載入失敗');
      }
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, [userSearchFilter, startDate, endDate, typeFilter]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // 處理篩選
  const handleFilter = () => {
    loadTransactions(1);
  };

  // 清除篩選
  const handleClearFilter = () => {
    setUserSearchFilter('');
    setStartDate('');
    setEndDate('');
    setTypeFilter('all');
  };

  // 處理分頁
  const handleChangePage = (_: unknown, newPage: number) => {
    loadTransactions(newPage + 1);
  };

  // 取得唯一的交易類型列表
  const uniqueTypes = Array.from(new Set(typeStats.map((s) => s.type)));

  return (
    <AdminLayout>
      <Box>
        {/* 頁面標題 */}
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          交易紀錄管理
        </Typography>

        {/* 統計卡片 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {typeStats.slice(0, 4).map((stat) => (
            <Grid item xs={6} md={3} key={stat.type}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="text.secondary" variant="body2">
                    {transactionTypeLabels[stat.type] || stat.type}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {stat.count} 筆
                  </Typography>
                  <Typography
                    variant="body2"
                    color={stat.total_amount >= 0 ? 'success.main' : 'error.main'}
                  >
                    {stat.total_amount >= 0 ? '+' : ''}
                    {stat.total_amount?.toFixed(0) || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 篩選區域 */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <FilterList color="primary" />
            <Typography variant="h6">篩選條件</Typography>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="搜尋使用者（Email 或姓名）"
                type="text"
                value={userSearchFilter}
                onChange={(e) => setUserSearchFilter(e.target.value)}
                size="small"
                placeholder="輸入 Email 或姓名"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="開始日期"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="結束日期"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>交易類型</InputLabel>
                <Select
                  value={typeFilter}
                  label="交易類型"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">全部類型</MenuItem>
                  {uniqueTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {transactionTypeLabels[type] || type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  onClick={handleFilter}
                  startIcon={<Search />}
                >
                  搜尋
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilter}
                >
                  清除
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => loadTransactions(pagination.page)}
                  startIcon={<Refresh />}
                >
                  重新整理
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* 錯誤提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 交易列表 */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>使用者</TableCell>
                  <TableCell>類型</TableCell>
                  <TableCell align="right">金額</TableCell>
                  <TableCell align="right">餘額變化</TableCell>
                  <TableCell>說明</TableCell>
                  <TableCell>時間</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">沒有交易紀錄</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((tx) => (
                    <TableRow key={tx.transaction_id} hover>
                      <TableCell>{tx.transaction_id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {tx.user_name || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tx.user_email} (ID: {tx.user_id})
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transactionTypeLabels[tx.type] || tx.type}
                          color={getTypeColor(tx.type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight="bold"
                          color={tx.amount >= 0 ? 'success.main' : 'error.main'}
                        >
                          {tx.amount >= 0 ? '+' : ''}
                          {tx.amount}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {tx.balance_before} → {tx.balance_after}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={applyPeriodToTransactionDesc(tx.description)}
                        >
                          {applyPeriodToTransactionDesc(tx.description) || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(tx.created_at).toLocaleString('zh-TW')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 分頁 */}
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={pagination.page_size}
            rowsPerPageOptions={[20]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} / 共 ${count} 筆`
            }
          />
        </Paper>
      </Box>
    </AdminLayout>
  );
};

export default TransactionManagementPage;
